import { apiErrorCode, apiErrorText } from 'src/utils/apiErrorText';

// The courses and tracks linked to a diploma (diploma-courses-tracks-multiselect
// D1-D3). Pure helpers for LinkedItemsField and DiplomaNewEditForm.
//
// The old form was an Autocomplete over `GET /courses` (active Kayfa courses
// only) whose value was the stored ids FILTERED by those options, so touching
// it silently dropped every linked inactive or partner course (and revoked the
// bootcamp's students' access to it); it showed the links in catalogue order
// instead of the stored order; and it saved from a list row loaded once.
// Now:
// - the rows come from the RAW stored ids (`GET /diplomas/link-options
//   ?diploma=<id>` → `linked`), in stored order, never dropped because an
//   option is missing ("Not found" row for a deleted course/track);
// - order is changed with ↑ / ↓, adding is a separate single-select;
// - only a changed list is sent, with `linksBase` (the loaded arrays) so the
//   API refuses a save over someone else's change (409 diploma_links_changed),
//   and a removal from a bootcamp with students needs a confirmation
//   (409 diploma_unlink_confirm → resend with `confirmUnlink: true`).

export type LinkKind = 'courses' | 'tracks';
export type DiplomaLinks = { courses: string[]; tracks: string[] };

export type LinkOption = {
  _id: string;
  name?: string;
  title?: string;
  title_ar?: string;
  active?: boolean;
  partner?: boolean;
  order?: number | null;
};

export type LinkOptions = {
  courses: LinkOption[];
  tracks: LinkOption[];
  // Present when the options were asked for one diploma (?diploma=<id>).
  linked?: DiplomaLinks;
};

export type LinkTag = 'Inactive' | 'Partner' | 'Not found';
export type LinkRow = { id: string; label: string; tags: LinkTag[] };

export const LINKS_CHANGED_CODE = 'diploma_links_changed';
export const UNLINK_CONFIRM_CODE = 'diploma_unlink_confirm';
export const LINKS_UNAVAILABLE_TEXT =
  'Course and track links can be changed after the server update.';
export const DIPLOMA_NOT_FOUND_TEXT = 'Diploma not found';

const SINGULAR: Record<LinkKind, string> = { courses: 'course', tracks: 'track' };

export const EMPTY_LINKS: DiplomaLinks = { courses: [], tracks: [] };

/** Ids as strings, nullish dropped (a populated ref gives its `_id`). */
export function idList(list: unknown): string[] {
  if (!Array.isArray(list)) return [];
  return list
    .map((x) => (x && typeof x === 'object' ? (x as any)._id : x))
    .filter((x) => x !== null && x !== undefined && x !== '')
    .map((x) => String(x));
}

/** The `GET /diplomas/link-options` body (`{ data }` envelope or not) as LinkOptions. */
export function readLinkOptions(body: any): LinkOptions {
  const data = body?.data ?? body ?? {};
  const options = (list: unknown): LinkOption[] =>
    (Array.isArray(list) ? list : [])
      .filter((o: any) => o && o._id)
      .map((o: any) => ({ ...o, _id: String(o._id) }));
  const out: LinkOptions = { courses: options(data.courses), tracks: options(data.tracks) };
  if (data.linked && typeof data.linked === 'object') {
    out.linked = { courses: idList(data.linked.courses), tracks: idList(data.linked.tracks) };
  }
  return out;
}

/** The name staff know an option by. */
export function optionLabel(kind: LinkKind, o?: Partial<LinkOption> | null): string {
  if (!o) return '';
  return kind === 'courses' ? o.name || o.title || '' : o.title || o.title_ar || o.name || '';
}

const tagsOf = (o: Partial<LinkOption>): LinkTag[] => {
  const tags: LinkTag[] = [];
  if (o.active === false) tags.push('Inactive');
  if (o.partner) tags.push('Partner');
  return tags;
};

/** "Course X (Inactive, Partner)" for the add picker. */
export function optionText(kind: LinkKind, o: LinkOption): string {
  const tags = tagsOf(o);
  const label = optionLabel(kind, o) || o._id;
  return tags.length ? `${label} (${tags.join(', ')})` : label;
}

/**
 * One row per stored id, in stored order. The label comes from the options,
 * else from the populated diploma (a course the options no longer offer),
 * else the row says "Not found" with the id (deleted course / track).
 */
export function buildRows(
  kind: LinkKind,
  ids: string[],
  options: LinkOption[],
  populated?: unknown[]
): LinkRow[] {
  const byId = new Map<string, Partial<LinkOption>>();
  (Array.isArray(populated) ? populated : []).forEach((p: any) => {
    if (p && typeof p === 'object' && p._id) byId.set(String(p._id), p);
  });
  options.forEach((o) => byId.set(String(o._id), o));
  return ids.map((id) => {
    const o = byId.get(id);
    if (!o) return { id, label: id, tags: ['Not found'] as LinkTag[] };
    return { id, label: optionLabel(kind, o) || id, tags: tagsOf(o) };
  });
}

/** The options not linked yet, for "Add a course…". */
export function addableOptions(options: LinkOption[], ids: string[]): LinkOption[] {
  const taken = new Set(ids);
  return options.filter((o) => !taken.has(String(o._id)));
}

/** One step up (-1) or down (+1); unchanged copy at the ends or for an unknown id. */
export function moveId(ids: string[], id: string, dir: -1 | 1): string[] {
  const out = [...ids];
  const i = out.indexOf(id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= out.length) return out;
  [out[i], out[j]] = [out[j], out[i]];
  return out;
}

/** Appended at the end; a duplicate is ignored. */
export function addId(ids: string[], id: string): string[] {
  if (!id || ids.includes(id)) return [...ids];
  return [...ids, id];
}

export function removeId(ids: string[], id: string): string[] {
  return ids.filter((x) => x !== id);
}

/** Same ids in the same order. */
export function sameIds(a: unknown, b: unknown): boolean {
  const x = idList(a);
  const y = idList(b);
  return x.length === y.length && x.every((id, i) => id === y[i]);
}

/** The base ids missing from `current` put back at their base position (Cancel on the unlink dialog). */
export function restoreRemoved(current: string[], base: string[]): string[] {
  const out = [...current];
  base.forEach((id, i) => {
    if (!out.includes(id)) out.splice(Math.min(i, out.length), 0, id);
  });
  return out;
}

export function linksChanged(links: DiplomaLinks, base: DiplomaLinks | null): boolean {
  if (!base) return false;
  return !sameIds(links.courses, base.courses) || !sameIds(links.tracks, base.tracks);
}

export type LinksPatch = Partial<DiplomaLinks> & { linksBase?: DiplomaLinks };

/**
 * What a save adds to the PATCH body: nothing when neither list changed (an
 * order-only change counts), else the changed list(s) and `linksBase` (both
 * loaded arrays — the API compares both). `base` null = the lists could not
 * be loaded (read-only), so nothing is ever sent.
 */
export function linksPatch(links: DiplomaLinks, base: DiplomaLinks | null): LinksPatch {
  if (!base) return {};
  const patch: LinksPatch = {};
  if (!sameIds(links.courses, base.courses)) patch.courses = [...links.courses];
  if (!sameIds(links.tracks, base.tracks)) patch.tracks = [...links.tracks];
  if (!patch.courses && !patch.tracks) return {};
  patch.linksBase = { courses: [...base.courses], tracks: [...base.tracks] };
  return patch;
}

/** Create: the chosen lists (no base, nothing to guard). */
export function createLinks(links: DiplomaLinks): Partial<DiplomaLinks> {
  const out: Partial<DiplomaLinks> = {};
  if (links.courses.length) out.courses = [...links.courses];
  if (links.tracks.length) out.tracks = [...links.tracks];
  return out;
}

export type RemovedLink = {
  scope: 'course' | 'track';
  id: string;
  title: string | null;
  holders: number;
};
export type UnlinkConfirm = { enrolled: number; removed: RemovedLink[]; message: string };

/** A save's 409 about the links: 'changed', the unlink confirmation, or null. */
export function readLinkConflict(err: any): 'changed' | UnlinkConfirm | null {
  const code = apiErrorCode(err);
  if (code === LINKS_CHANGED_CODE) return 'changed';
  if (code !== UNLINK_CONFIRM_CODE) return null;
  const detail = err?.detail || {};
  const removed: RemovedLink[] = (Array.isArray(detail.removed) ? detail.removed : []).map(
    (r: any) => ({
      scope: r?.scope === 'track' ? 'track' : 'course',
      id: String(r?.id ?? ''),
      title: typeof r?.title === 'string' && r.title ? r.title : null,
      holders: Number(r?.holders) || 0,
    })
  );
  return { enrolled: Number(detail.enrolled) || 0, removed, message: apiErrorText(err, '') };
}

const students = (n: number) => `${n} student${n === 1 ? '' : 's'}`;

/** "QA Course X — 1 student with access" ("Not found" for a deleted item). */
export function removedLine(r: RemovedLink): string {
  return `${r.title ?? 'Not found'} — ${students(r.holders)} with access`;
}

/** "1 student is enrolled in this bootcamp" / "3 students are enrolled …" ('' for none). */
export function enrolledLine(n: number): string {
  if (!n) return '';
  return `${students(n)} ${n === 1 ? 'is' : 'are'} enrolled in this bootcamp`;
}

/**
 * The admin's own link changes in words, for the "changed by someone else"
 * box (so they can be redone after Reload): "Added course: B. Removed track: 1.
 * Changed the order of the courses."
 */
export function describeLinkChanges(
  links: DiplomaLinks,
  base: DiplomaLinks | null,
  labelOf: (kind: LinkKind, id: string) => string
): string {
  if (!base) return '';
  const parts: string[] = [];
  (['courses', 'tracks'] as LinkKind[]).forEach((kind) => {
    const now = links[kind];
    const was = base[kind];
    const added = now.filter((id) => !was.includes(id));
    const removed = was.filter((id) => !now.includes(id));
    const names = (ids: string[]) => ids.map((id) => labelOf(kind, id)).join(', ');
    if (added.length)
      parts.push(`Added ${SINGULAR[kind]}${added.length > 1 ? 's' : ''}: ${names(added)}.`);
    if (removed.length)
      parts.push(`Removed ${SINGULAR[kind]}${removed.length > 1 ? 's' : ''}: ${names(removed)}.`);
    const kept = now.filter((id) => was.includes(id));
    const keptBefore = was.filter((id) => now.includes(id));
    if (!sameIds(kept, keptBefore)) parts.push(`Changed the order of the ${kind}.`);
  });
  return parts.join(' ');
}

/**
 * How the link-options read failed: the diploma is gone ('notFound', the form
 * does not open), or the API does not have the route yet ('unavailable': an
 * older API answers `/diplomas/link-options` with its `:id` route's 400
 * "Invalid ID" or a 404 "Cannot GET"), or anything else ('failed').
 */
export function linkOptionsFailure(err: any): 'notFound' | 'unavailable' | 'failed' {
  const text = apiErrorText(err, '');
  if (/Diploma does not exist/i.test(text)) return 'notFound';
  if (/Invalid ID provided|Cannot GET/i.test(text)) return 'unavailable';
  return 'failed';
}
